import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  FREE_DAILY_AI_MSG_CAP,
  FREE_MONTHLY_AI_MSG_CAP,
  isFreeTier,
  type PlanTier,
  ttsLimitFor,
} from "@/config/quotas";

export interface QuotaMeterState {
  plan: PlanTier;
  isFree: boolean;
  /**
   * UNIDADES PONDERADAS consumidas, não contagem de chamadas (06/08). Cada
   * chamada soma chat.surface_weights[superfície] — conversa com o Espelho
   * 1,0; documento 2,0; agenda/tarefa 0,5; sleep/language 0,1. O medidor
   * mostra unidades porque é o que o gate compara: exibir "12 mensagens"
   * enquanto o servidor bloqueia em 12,5 unidades seria um número que não
   * bate com o que trava.
   */
  textUsedToday: number;
  textDailyLimit: number;
  textUsedMonth: number;
  textMonthlyLimit: number;
  // Voice TTS (all tiers).
  ttsUsedSeconds: number;
  ttsLimitSeconds: number;
  isLoading: boolean;
  refresh: () => void;
}

function asPlan(v: unknown): PlanTier {
  return v === "premium" || v === "pro" || v === "ultra" ? v : "free";
}

// numeric do Postgres chega como number OU string dependendo do serializador.
function num(v: unknown, fallback = 0): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/**
 * Uso de cota do usuário, lido de quota_usage_snapshot.
 *
 * NÃO calcula mais a virada de período (06/08). Este hook tinha a TERCEIRA
 * cópia da fronteira do mês — `Date.UTC(...)`, ao lado da do edge e da do SQL.
 * As três concordavam só por serem UTC; a troca para BRT quebraria duas delas
 * exatamente na virada do mês. Agora o rollover vem resolvido do SQL, pelas
 * mesmas quota_month_start/quota_day_start que as consume_* usam.
 *
 * Os TETOS também vêm de lá (agent_config). Antes eram as constantes locais:
 * mudar chat.max_per_month para 100 travava em 100 e esta tela seguia
 * dizendo /40 — o medidor mentia sobre o próprio limite. As constantes ficam
 * como fallback para quando a chave não existe para o tier.
 */
export function useQuotaMeter(): QuotaMeterState {
  const [state, setState] = useState<Omit<QuotaMeterState, "refresh">>({
    plan: "free",
    isFree: true,
    textUsedToday: 0,
    textDailyLimit: FREE_DAILY_AI_MSG_CAP,
    textUsedMonth: 0,
    textMonthlyLimit: FREE_MONTHLY_AI_MSG_CAP,
    ttsUsedSeconds: 0,
    ttsLimitSeconds: ttsLimitFor("free"),
    isLoading: true,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (!cancelled) setState((s) => ({ ...s, isLoading: false }));
        return;
      }
      const { data, error } = await supabase.rpc("quota_usage_snapshot", { p_user_id: user.id });
      if (cancelled) return;
      const rows = (data ?? []) as Record<string, unknown>[];
      const row = rows.length > 0 ? rows[0] : null;
      if (error || !row) {
        // Medidor é display-only: sem dado ele some, não inventa número. O
        // erro precisa aparecer — este é o caminho que ficaria mudo se o
        // deploy da migration não tivesse acontecido (§11).
        console.error("[useQuotaMeter] quota_usage_snapshot falhou", error);
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      const plan = asPlan(row.plan);
      // cap_* vem null quando o tier não está no mapa do agent_config →
      // fallback de código, o mesmo número que o backend usaria.
      const capMonth = row.cap_month === null || row.cap_month === undefined
        ? FREE_MONTHLY_AI_MSG_CAP
        : num(row.cap_month, FREE_MONTHLY_AI_MSG_CAP);
      const capDay = row.cap_day === null || row.cap_day === undefined
        ? FREE_DAILY_AI_MSG_CAP
        : num(row.cap_day, FREE_DAILY_AI_MSG_CAP);
      const ttsMinutes = row.tts_minutes === null || row.tts_minutes === undefined
        ? null
        : num(row.tts_minutes, 0);

      setState({
        plan,
        isFree: isFreeTier(plan),
        textUsedToday: num(row.msgs_today),
        textDailyLimit: capDay,
        textUsedMonth: num(row.msgs_month),
        textMonthlyLimit: capMonth,
        ttsUsedSeconds: num(row.tts_seconds),
        ttsLimitSeconds: ttsMinutes === null ? ttsLimitFor(plan) : Math.round(ttsMinutes * 60),
        isLoading: false,
      });
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { ...state, refresh };
}
